import React from 'react';
import {
  Bar,
  BarChart as ReactBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import styled from '@emotion/styled';
import { RechartsDevtools } from '@recharts/devtools';

type ChartDataPoint = Record<string, string | number | null | undefined>;

type BarRadius = number | [number, number, number, number];

export interface StackedBarSeriesConfig {
  dataKey: string;
  label?: string;
  color: string;
  stackId?: string;
  radius?: BarRadius;
  hideInLegend?: boolean;
}

interface TooltipPayloadItem {
  name?: string;
  dataKey?: string;
  value?: number | string;
  color?: string;
}

interface LegendPayloadItem {
  value?: string;
  color?: string;
  dataKey?: string;
}

interface StackedTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  showTotal?: boolean;
  totalLabel?: string;
  valueFormatter?: (value: number, dataKey: string) => React.ReactNode;
}

interface StackedLegendProps {
  payload?: LegendPayloadItem[];
  order?: 'normal' | 'reverse';
}

export interface StackedBarChartProps {
  data: ChartDataPoint[];
  xAxisDataKey: string;
  series: StackedBarSeriesConfig[];
  isAnimationActive?: boolean;
  aspect?: number;
  maxHeight?: number;
  showLegend?: boolean;
  legendOrder?: 'normal' | 'reverse';
  showTooltip?: boolean;
  tooltipShowTotal?: boolean;
  tooltipTotalLabel?: string;
  tooltipValueFormatter?: (value: number, dataKey: string) => React.ReactNode;
  showGrid?: boolean;
  gridStrokeDasharray?: string;
  xAxisTickFormatter?: (value: string | number) => string;
  yAxisTickFormatter?: (value: string | number) => string;
  margin?: {
    top?: number;
    right?: number;
    left?: number;
    bottom?: number;
  };
  barSize?: number;
  categoryGap?: string | number;
  showDevTools?: boolean;
}

const buildDefaultRadius = (
  currentIndex: number,
  totalItems: number,
): [number, number, number, number] =>
  currentIndex === totalItems - 1 ? [12, 12, 0, 0] : [0, 0, 0, 0];

const defaultValueFormatter = (value: number): number => value;

const StackedTooltip: React.FC<StackedTooltipProps> = ({
  active,
  payload,
  label,
  showTotal = true,
  totalLabel = 'Total',
  valueFormatter = defaultValueFormatter,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const rows = [...payload].reverse();
  const total = rows.reduce((sum, item) => {
    const numericValue = Number(item.value ?? 0);
    return Number.isNaN(numericValue) ? sum : sum + numericValue;
  }, 0);

  return (
    <TooltipContainer>
      {label !== undefined && label !== null && (
        <TooltipHeader>{String(label)}</TooltipHeader>
      )}

      {rows.map((entry) => {
        const numericValue = Number(entry.value ?? 0);
        const safeValue = Number.isNaN(numericValue) ? 0 : numericValue;
        const seriesKey = entry.dataKey ?? entry.name ?? '';

        return (
          <TooltipRow key={`${seriesKey}-${entry.name}`}>
            <TooltipLabel>{entry.name ?? seriesKey}</TooltipLabel>
            <TooltipValue>{valueFormatter(safeValue, seriesKey)}</TooltipValue>
          </TooltipRow>
        );
      })}

      {showTotal && (
        <>
          <TooltipDivider />
          <TooltipRow>
            <TooltipTotalLabel>{totalLabel}</TooltipTotalLabel>
            <TooltipTotalValue>
              {valueFormatter(total, 'total')}
            </TooltipTotalValue>
          </TooltipRow>
        </>
      )}
    </TooltipContainer>
  );
};

const StackedLegend: React.FC<StackedLegendProps> = ({
  payload,
  order = 'normal',
}) => {
  if (!payload || payload.length === 0) {
    return null;
  }

  const items = order === 'reverse' ? [...payload].reverse() : payload;

  return (
    <LegendContainer>
      {items.map((item) => (
        <LegendItem key={`${item.dataKey}-${item.value}`}>
          <LegendDot color={item.color ?? '#9CA3AF'} />
          <LegendText color={item.color ?? '#6B7280'}>{item.value}</LegendText>
        </LegendItem>
      ))}
    </LegendContainer>
  );
};

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  xAxisDataKey,
  series,
  isAnimationActive = true,
  aspect = 4,
  maxHeight = 500,
  showLegend = true,
  legendOrder = 'reverse',
  showTooltip = true,
  tooltipShowTotal = true,
  tooltipTotalLabel = 'Total',
  tooltipValueFormatter = defaultValueFormatter,
  showGrid = true,
  gridStrokeDasharray = '0',
  xAxisTickFormatter,
  yAxisTickFormatter,
  margin = {
    top: 20,
    right: 24,
    left: 0,
    bottom: 16,
  },
  barSize = 36,
  categoryGap = '25%',
  showDevTools = false,
}) => {
  const visibleSeries = series;

  return (
    <ResponsiveContainer width='100%' aspect={aspect} maxHeight={maxHeight}>
      <ReactBarChart
        responsive
        data={data}
        margin={margin}
        barSize={barSize}
        barCategoryGap={categoryGap}
      >
        {showGrid && (
          <CartesianGrid
            strokeDasharray={gridStrokeDasharray}
            fill={'#ffffff'}
            stroke={'#E0E0E0'}
            vertical={false}
          />
        )}

        <XAxis
          dataKey={xAxisDataKey}
          interval={'preserveEnd'}
          tickMargin={16}
          tickSize={0}
          stroke={'#E0E0E0'}
          tickFormatter={xAxisTickFormatter}
          style={{
            fontSize: '10px',
            fill: '#71748C',
            fontWeight: '700',
          }}
        />

        <YAxis
          width='auto'
          tickSize={0}
          tickMargin={12}
          stroke='#E0E0E00'
          tickFormatter={yAxisTickFormatter}
          style={{
            fontSize: '10px',
            fill: '#71748C',
            fontWeight: '700',
          }}
        />

        {showTooltip && (
          <Tooltip
            cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
            content={
              <StackedTooltip
                showTotal={tooltipShowTotal}
                totalLabel={tooltipTotalLabel}
                valueFormatter={tooltipValueFormatter}
              />
            }
          />
        )}

        {showLegend && (
          <Legend
            verticalAlign='bottom'
            align='center'
            content={<StackedLegend order={legendOrder} />}
          />
        )}

        {visibleSeries.map((item, index) => (
          <Bar
            key={item.dataKey}
            dataKey={item.dataKey}
            name={item.label ?? item.dataKey}
            fill={item.color}
            stackId={item.stackId ?? 'total'}
            radius={
              item.radius ?? buildDefaultRadius(index, visibleSeries.length)
            }
            isAnimationActive={isAnimationActive}
            maxBarSize={100}
            legendType={item.hideInLegend ? 'none' : 'circle'}
          />
        ))}

        {showDevTools && <RechartsDevtools />}
      </ReactBarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart;

const TooltipContainer = styled.div`
  background: ${({ theme }) => theme.palette.whiteBg};
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0px 6px 16px ${({ theme }) => theme.palette.boxShadowColor};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 170px;
`;

const TooltipHeader = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.mediumGrayText};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const TooltipLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.secondaryTextColor};
`;

const TooltipValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackColor};
`;

const TooltipDivider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
`;

const TooltipTotalLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.secondaryTextColor};
`;

const TooltipTotalValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.blackColor};
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 36px;
  margin-top: 18px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const LegendText = styled.span<{ color: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.color};
`;
